import { toBase64 } from 'js-base64'
import { round, isEqual, omitBy } from 'lodash'
import { nanoid } from 'nanoid'
import * as z from 'zod/v4-mini'
import { create } from 'zustand'
import { persist } from 'zustand/middleware'

import { Currency } from '../../backend'
import { getSamplePrice } from '../logic/price'
import { storage } from '../logic/zustand'

import { PlainProduct } from './useProducts'

export const ShoppingCartItem = z.object({
  id: z.string(),
  parameters: z.record(z.string(), z.string()),
  productId: z.string(),
  samples: z.optional(z.array(z.string())),
})
export type ShoppingCartItem = z.infer<typeof ShoppingCartItem>

export const NewShoppingCartItem = z.omit(ShoppingCartItem, {
  id: true,
  samples: true,
})
export type NewShoppingCartItem = z.infer<typeof NewShoppingCartItem>

export const ShoppingCartSample = z.object({
  id: z.string(),
  material: z.string(),
  name: z.string(),
})
export type ShoppingCartSample = z.infer<typeof ShoppingCartSample>

export const ShoppingCartPathData = z.tuple([
  z.array(
    z.partial(z.omit(ShoppingCartItem, { id: true }), { parameters: true }),
  ),
  z.array(z.partial(ShoppingCartSample, { material: true, name: true })),
])
export type ShoppingCartPathData = z.infer<typeof ShoppingCartPathData>

export interface ShoppingCartItemWithProduct extends ShoppingCartItem {
  product: PlainProduct
}

export const PaymentMethod = z.optional(
  z.enum(['bankTransfer', 'creditCard', 'eInvoice']),
)
export type PaymentMethod = z.infer<typeof PaymentMethod>

const V0BillingInformation = z.object({
  billingAddress: z.string(),
  billingReference: z.string(),
  vatNumber: z.string(),
})
const StateV0 = z.object({
  additionalDetails: z.string(),
  billingInformation: V0BillingInformation,
  items: z.array(ShoppingCartItem),
  samples: z.array(ShoppingCartSample),
})
const V1BillingInformation = z.extend(V0BillingInformation, {
  billingCountry: z.string(),
  billingEmail: z.string(),
  eInvoiceOperator: z.string(),
  eInvoiceRoutingNumber: z.string(),
})
const StateV1 = z.extend(StateV0, {
  billingInformation: V1BillingInformation,
})
const StateV2 = z.extend(StateV1, {
  billingInformation: z.extend(
    z.omit(V1BillingInformation, { billingCountry: true }),
    { paymentMethod: PaymentMethod },
  ),
})

const states = [StateV0, StateV1, StateV2] satisfies [unknown, ...unknown[]]
const States = z.tuple(states)
type States = z.infer<typeof States>

type State = States extends [...unknown[], infer S] ? S : never

interface Setters {
  addItem(item: NewShoppingCartItem): boolean
  addSample(item?: Omit<ShoppingCartSample, 'id'>): void
  removeItem(id: string): void
  removeSample(id: string): void
  setAdditionalDetails(value: string): void
  updateBillingInformation(payload: Partial<State['billingInformation']>): void
  updateItem(item: { id: string } & Partial<Omit<ShoppingCartItem, 'id'>>): void
  updateSample(
    item: { id: string } & Partial<Omit<ShoppingCartSample, 'id'>>,
  ): void
  clearCart(): void
  setFromPathData(data: ShoppingCartPathData): void
}
interface Getters {
  hasItem(item: Pick<ShoppingCartItem, 'productId' | 'parameters'>): boolean
}

export const cartToBase64 = ({
  samples,
  items,
}: Pick<State, 'items' | 'samples'>) =>
  toBase64(
    JSON.stringify([
      items.map(({ id, parameters, ...item }) =>
        Object.keys(parameters).length === 0 ? item : { ...item, parameters },
      ),
      samples.map(sample => omitBy(sample, value => !value)),
    ]),
    true,
  )

export const getItemPrice = (
  { product, samples, parameters }: Required<ShoppingCartItemWithProduct>,
  currency: Currency,
) => {
  if (!samples.length || !product || !product.fixedUnitPriceCents) {
    return 0
  }
  return round(
    (getSamplePrice({ currency, parameters, product }) ?? 0) * samples.length,
    2,
  )
}

export const getTurnaroundWeeks = (
  product: Pick<PlainProduct, 'tatParameter' | 'turnaroundWeeks'>,
  parameters: Record<string, string>,
) =>
  product.tatParameter?.options.find(
    option => option.id === parameters[product.tatParameter!.id],
  )?.turnaroundWeeks ?? product.turnaroundWeeks

const isEqualItem = (
  { samples: aSamples, id: aKey, ...a }: Partial<ShoppingCartItem>,
  { samples: bSamples, id: bKey, ...b }: Partial<ShoppingCartItem>,
) => isEqual(a, b)

const emptyCartState: Pick<State, 'items' | 'samples' | 'additionalDetails'> = {
  additionalDetails: '',
  items: [],
  samples: [{ id: nanoid(), material: '', name: '' }],
}

const initialState: State = {
  ...emptyCartState,
  billingInformation: {
    billingAddress: '',
    billingEmail: '',
    billingReference: '',
    eInvoiceOperator: '',
    eInvoiceRoutingNumber: '',
    paymentMethod: undefined,
    vatNumber: '',
  },
}

type Version = States extends [...infer S, unknown]
  ? S extends { length: infer L }
    ? L
    : never
  : never
const migrations = [
  (state: unknown) => {
    const s = StateV0.parse(state)
    return StateV1.parse({
      ...s,
      billingInformation: {
        ...s.billingInformation,
        billingCountry: '',
        billingEmail: '',
        eInvoiceOperator: '',
        eInvoiceRoutingNumber: '',
      },
    })
  },
  (state: unknown): State => {
    const {
      billingInformation: { billingCountry, ...billingInformation },
      ...s
    } = StateV1.parse(state)
    return StateV2.parse({
      ...s,
      billingInformation: {
        ...billingInformation,
        paymentMethod: billingInformation.eInvoiceRoutingNumber
          ? 'eInvoice'
          : 'bankTransfer',
      },
    })
  },
] satisfies [...unknown[], (persistedState: unknown) => State] satisfies {
  length: Version
}

export const useShoppingCart = create(
  persist<State & Setters & Getters>(
    (set, get) => ({
      ...initialState,
      updateBillingInformation: payload =>
        set(state => ({
          ...state,
          billingInformation: { ...state.billingInformation, ...payload },
        })),

      addItem: item => {
        if (get().hasItem(item)) {
          return false
        }
        set(state => ({
          items: [...state.items, { ...item, id: nanoid() }],
        }))
        return true
      },
      hasItem: item => get().items.some(i => isEqualItem(item, i)),
      removeItem: id =>
        set(state => ({
          items: state.items.filter(item => item.id !== id),
        })),
      updateItem: payload =>
        set(state => ({
          items: state.items.map(item =>
            item.id === payload.id ? { ...item, ...payload } : item,
          ),
        })),

      addSample: sample =>
        set(state => ({
          samples: [
            ...state.samples,
            { material: '', name: '', ...sample, id: nanoid() },
          ],
        })),
      removeSample: (id: string) =>
        set(state => ({
          items: state.items.map(item => ({
            ...item,
            samples: item.samples?.filter(sample => sample !== id),
          })),
          samples: state.samples.filter(sample => sample.id !== id),
        })),
      updateSample: payload =>
        set(state => ({
          samples: state.samples.map(sample =>
            sample.id === payload.id ? { ...sample, ...payload } : sample,
          ),
        })),

      setAdditionalDetails: (additionalDetails: string) =>
        set({ additionalDetails }),

      clearCart: () => set(() => emptyCartState),
      setFromPathData: ([items, samples]) =>
        set(() => ({
          additionalDetails: '',
          items: items.map(item => ({ parameters: {}, ...item, id: nanoid() })),
          samples: samples.map(sample => ({
            material: '',
            name: '',
            ...sample,
          })),
        })),
    }),
    {
      migrate: (persistedState, version) => {
        try {
          return migrations
            .slice(version)
            .reduce(
              (stateSoFar, migration) => migration(stateSoFar),
              persistedState,
            ) as State & Setters & Getters
        } catch {
          return initialState as State & Setters & Getters
        }
      },
      name: 'shoppingCart',
      storage,
      version: 2 satisfies Version,
    },
  ),
)

export const getItemsWithProducts = (
  items: State['items'],
  products: PlainProduct[],
) =>
  items
    .map(item => ({
      ...item,
      product: products.find(({ id }) => item.productId === id),
    }))
    // filter out missing products (e.g. deleted)
    .filter((item): item is ShoppingCartItemWithProduct =>
      Boolean(item.product),
    )

export const getTotalPrice = (
  items: ShoppingCartItemWithProduct[],
  samples: ShoppingCartSample[],
  currency: Currency,
) =>
  items.reduce(
    (prev, item) =>
      prev +
      getItemPrice(
        {
          ...item,
          samples: item.samples ?? samples.map(({ id }) => id),
        },
        currency,
      ),
    0,
  )

export const getTotalTurnaroundWeeks = (
  items: (ShoppingCartItem & {
    product: Pick<PlainProduct, 'tatParameter' | 'turnaroundWeeks'>
  })[],
) => {
  let tat = 0
  for (const item of items) {
    const productTat = getTurnaroundWeeks(item.product, item.parameters)
    if (productTat.min === null) {
      return null
    }
    tat = Math.max(tat, productTat.min ?? 0, productTat.max ?? 0)
  }
  return tat
}
